import { LogOut, X, AlertTriangle, Loader2 } from "lucide-react";
import { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../context/AuthContext";

const LogoutDialog = ({ isOpen, onClose, collapsed = false, centered = false }) => {
  const { logout } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const cancelRef = useRef(null);
  const popoverRef = useRef(null);

  // Track viewport: sidebar popover (desktop) vs centered modal (mobile).
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobileView(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    cancelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Desktop popover has no backdrop, so close on any click outside it.
  useEffect(() => {
    if (!isOpen || isMobileView || centered) return;
    const onPointerDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isOpen, isMobileView, centered, onClose]);

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      // Call your actual logout API here
      // await logout();
      logout();
    } catch (error) {
      console.error("Logout failed:", error);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Shared dialog inner content (close button, icon, copy, actions).
  const dialogContent = (
    <div className="relative">
      {/* Close Button */}
      <button
        className="absolute top-3.5 right-3.5 flex h-7 w-7 items-center justify-center rounded-full text-text_muted hover:bg-danger_bg hover:text-danger disabled:pointer-events-none disabled:opacity-50"
        onClick={onClose}
        disabled={isLoading}
        aria-label="Close"
      >
        <X size={15} />
      </button>

      {/* Body */}
      <div className="px-6 pt-7 pb-6">
        {/* Warning Icon */}
        <div className="mb-4 flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-danger_border bg-danger_bg">
            <AlertTriangle size={24} className="text-danger" strokeWidth={2} />
          </div>
        </div>

        {/* Title */}
        <h4
          id="logout-dialog-title"
          className="m-0 mb-1.5 text-center text-[17px] font-semibold text-text_primary"
        >
          Log out of your account?
        </h4>

        {/* Message */}
        <p className="m-0 text-center text-[13.5px] leading-relaxed text-text_secondary">
          You'll need to sign in again to access your dashboard.
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-2.5 border-t border-border bg-surface_alt px-6 py-4">
        <button
          ref={cancelRef}
          className="flex flex-1 items-center justify-center rounded-[10px] border border-border bg-surface px-4 py-2.5 text-[13px] font-medium text-text_secondary hover:border-border_strong hover:text-text_primary disabled:cursor-not-allowed disabled:opacity-60"
          onClick={onClose}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] bg-danger px-4 py-2.5 text-[13px] font-medium text-white hover:bg-danger/90 disabled:cursor-not-allowed disabled:opacity-70"
          onClick={handleLogout}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 size={15} className="flex-shrink-0 animate-spin" />
              Logging out…
            </>
          ) : (
            <>
              <LogOut size={15} className="flex-shrink-0" />
              Log out
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Mobile (or any non-sidebar caller, e.g. the Header profile dropdown):
  // centered modal with a blurred, dimmed backdrop
  if (isMobileView || centered) {
    return (
      <div
        className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px] animate-logout-fade"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-dialog-title"
      >
        <div
          className="w-full max-w-[340px] overflow-hidden rounded-xl border border-border bg-surface shadow-[0_20px_50px_rgba(0,0,0,0.18)] animate-logout-in"
          onClick={(e) => e.stopPropagation()}
        >
          {dialogContent}
        </div>
        <style>{logoutDialogKeyframes}</style>
      </div>
    );
  }

  // Desktop: popover anchored beside the sidebar
  return (
    <div
      ref={popoverRef}
      className={`absolute bottom-[15px] z-[1000] w-[300px] overflow-hidden rounded-xl border border-border bg-surface shadow-[0_20px_50px_rgba(0,0,0,0.18)] animate-logout-in ${
        collapsed ? "left-[60px]" : "left-[250px]"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-dialog-title"
    >
      {/* Arrow pointing to the sidebar logout button */}
      <span className="absolute -left-2 top-[80%] h-0 w-0 border-y-[9px] border-r-[8px] border-y-transparent border-r-surface" />
      {dialogContent}
      <style>{logoutDialogKeyframes}</style>
    </div>
  );
};

const logoutDialogKeyframes = `
  @keyframes logout-in {
    from { opacity: 0; transform: scale(0.95) translateY(6px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes logout-fade {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .animate-logout-in {
    animation: logout-in 0.16s cubic-bezier(0.34, 1.36, 0.64, 1) forwards;
  }
  .animate-logout-fade {
    animation: logout-fade 0.12s ease-out forwards;
  }
`;

export default LogoutDialog;
